import React, { useCallback, useEffect, useRef, useState } from "react";
import Heading from "../../../../components/Heading";
import Text from "../../../../components/Text";
import { useTranslation } from "react-i18next";
import { useIndividualizationContext } from "../../../../contexts/individualizationContext";
import { fabric } from "fabric";
import Icon from "../../../../components/Icon";
import {faChevronDown, faChevronUp} from "@fortawesome/free-solid-svg-icons";
import { v4 as uuid } from 'uuid';
import Card from "../../../../components/Card";
import RectangleSVG from "./SVGs/RectangleSVG";
import CircleSVG from "./SVGs/CircleSVG";
import EllipseSVG from "./SVGs/EllipseSVG";
import PolygonSVG from "./SVGs/PolygonSVG";
import cx from "classnames";
import styles from './DefineAreas.module.css'
import ActiveSpotCard from "./ActiveSpotCard";
import Sidebar from "./Sidebar";
import {
    changeBackground,
    resizeFabricObject
} from "../../../../helpers/resizeFabricObject";
import {
    getCircle, getEllipse, getPolygon, getRectangle
} from "../../../../helpers/addFabricElement";

const DefineAreas: React.FC = () => {
    const { t } = useTranslation(['individualization']);
    const {
        canvas,
        size,
        updateSize,
        activeStep,
        templates,
        spots,
        addSpot,
        removeSpot,
        syncSpot,
        syncHelper,
        addEdgeToPolygon,
        removeEdgeFromPolygon,
        helpers,
        addHelper,
        examples,
        setExamples,
        activeSpotId,
        activeSpot,
        changeActiveSpot,
        activeTemplateId,
        activeTemplate,
        fonts,
        changeActiveTemplate
    } = useIndividualizationContext()
    const wrapper = useRef<HTMLDivElement>(null)
    const [showTemplatesSidebar, setShowTemplatesSidebar] = useState<boolean>(templates.length > 1);
    const [showAddSectionSidebar, setShowAddSectionSidebar] = useState<boolean>(true)
    const resizeObserver = useRef<ResizeObserver | null>(null)
    const timer = useRef<undefined | number | NodeJS.Timeout>(undefined)

    const syncExample = useCallback((obj: fabric.Object) => {
        setExamples(prevExamples => [...prevExamples.filter((ex) => ex.data.spot_id !== obj.data.spot_id), obj])
    }, [setExamples])

    const changeExampleText = useCallback((spot_id: string|number, data: Partial<fabric.Text>) => {

        const spot = spots.find((s) => s.data?.id === spot_id)
        if (!spot) return

        let example = examples.find((e) => e.data.spot_id === spot_id)

        if (!example) {
           example = new fabric.Text('', {
               originX: 'center',
               originY: 'center',
               left: spot.left,
               top: spot.top,
               angle: spot.angle,
               fontSize: spot.data?.font_size ?? 40,
               fontFamily: fonts.length > 0 ? fonts[0].name : undefined,
               fill: spot.data.text_color ?? '#000',
               selectable: true,
               centeredScaling: true,
               hasBorders: false,
               hasControls: false,
               cornerSize: 12,
               data: {
                   spot_id: spot_id,
                   template_id: spot.data?.template_id,
                   size: size,
                   type: 'Example',
               }
           })

           canvas.add(example)
        }

        example.set(data)

        setExamples((prevExamples) => {
            if (example) return [...prevExamples.filter((e) => e.data?.spot_id !== spot_id), example]
            else return prevExamples
        })

        canvas.requestRenderAll()

    }, [examples, spots, setExamples])

    const changePage = (template_id: string|number) => {
        const result = templates.find((template) => template.id === template_id)
        if (result && template_id !== activeTemplateId) {
            changeActiveTemplate(template_id)
            changeActiveSpot(null)
        }
    }

    const resizeEverything = useCallback((sizing: number) => {
        canvas.setWidth(sizing - 2)
        canvas.setHeight(sizing - 2)

        if (canvas.backgroundImage && canvas.backgroundImage instanceof fabric.Image){
            const scaleRatio = Math.min(
                sizing / canvas.backgroundImage.getOriginalSize().width,
                sizing / canvas.backgroundImage.getOriginalSize().height
            );

            canvas.backgroundImage.set({
                scaleX: scaleRatio,
                scaleY: scaleRatio,
                top: sizing/2,
                left: sizing/2
            })
        }

        spots.forEach((object) => {
            resizeFabricObject(object, sizing)
            syncSpot(object)
        })

        helpers.forEach((object) => {
            if (object.data?.size !== sizing) {
                resizeFabricObject(object, sizing)
                syncHelper(object)
            }
        })

        examples.forEach((object) => {
            if (object.data?.size !== sizing) {
                resizeFabricObject(object, sizing)
                syncExample(object)
            }
        })

    }, [spots, helpers, examples, syncExample, syncSpot, syncHelper, activeTemplate])

    useEffect(() => {
        if (activeStep === 2 && wrapper.current) {
            if (templates.length > 0 ) changeActiveTemplate(templates[0].id)
            else changeActiveTemplate(null)

            const size = wrapper.current.getBoundingClientRect().width
            canvas.setWidth(size - 2).setHeight(size - 2)
            wrapper.current.append(canvas.getElement())
            wrapper.current.append(canvas.getSelectionElement())

            resizeObserver.current = new ResizeObserver(() => {
                clearTimeout(timer.current)
                timer.current = setTimeout(() => {
                    if (wrapper.current) updateSize(wrapper.current.getBoundingClientRect().width)
                }, 500)
            });

            updateSize(size)
            resizeObserver.current.observe(wrapper.current)
        }
    }, [activeStep, templates])

    useEffect(() => {
        if (size) resizeEverything(size)
    }, [size])

    useEffect(() => {
        if (activeStep === 2 && activeTemplate) {
            changeBackground(activeTemplate, canvas)
        }
    }, [activeStep, activeTemplate, canvas])

    useEffect(() => {
        canvas.remove(...canvas.getObjects().filter((obj) => obj.data?.template_id !== activeTemplateId))

        spots.forEach((spot) => {
            if (spot.data.template_id === activeTemplateId) {
                const found = canvas.getObjects().find((obj) => obj.data?.id === spot.data.id)
                if (!found) canvas.add(spot)
            }
        })
        helpers.forEach((helper) => {
            if (helper.data.template_id === activeTemplateId) {
                const found = canvas.getObjects().find((obj) => obj.data?.id === helper.data.id)
                if (!found) canvas.add(helper)
            }
        })

    }, [spots, helpers, activeTemplateId])

    const addCircle = () => {
        if (activeTemplateId) {
            const circle = getCircle(canvas, size, activeTemplateId)
            addSpot(circle)
            canvas.add(circle)
            canvas.setActiveObject(circle)
        }
    }

    const addEllipse = () => {
        if (activeTemplateId) {
            const ellipse = getEllipse(canvas, size, activeTemplateId)
            addSpot(ellipse)
            canvas.add(ellipse)
            canvas.setActiveObject(ellipse)
        }
    }

    const addRectangle = () => {
        if (activeTemplateId) {
            const rectangle = getRectangle(canvas, size, activeTemplateId)
            addSpot(rectangle)
            canvas.add(rectangle)
            canvas.setActiveObject(rectangle)
        }
    }

    const addPolygon = () => {
        const id = uuid()
        if (activeTemplateId) {
            const polygon = getPolygon(canvas, size, activeTemplateId, id)
            addSpot(polygon)
            canvas.add(polygon)
            canvas.setActiveObject(polygon)

            if (polygon.points) polygon.points.forEach((point: fabric.Point, index: number) => {
                addEdge(point, index, id)
            })
        }
    }

    const addEdge = (point: fabric.Point, index: number, polygon_id: string | number) => {
        const edge = new fabric.Circle({
            originX: 'center',
            originY: 'center',
            left: point.x,
            top: point.y,
            radius: 6,
            angle: 0,
            fill: '#FC0',
            centeredScaling: true,
            selectable: true,
            transparentCorners: true,
            hasBorders: false,
            hasControls: false,
            hasRotatingPoint: false,
            data: {
                id: uuid(),
                type: 'Edge',
                index: index,
                template_id: activeTemplateId,
                polygon_id: polygon_id,
                size: size,
            },
            opacity: 1
        })

        addHelper(edge)
        canvas.add(edge)
        canvas.requestRenderAll()
    }

    const changeFabricActive = (obj: fabric.Object) => {
        if (obj instanceof fabric.Polygon) {
            const circleList = canvas.getObjects('circle')
            const edge = circleList.find((p) => p.data?.polygon_id === obj.data.id)
            if (edge) canvas.setActiveObject(edge)
        } else {
            canvas.setActiveObject(obj)
        }
        canvas.requestRenderAll()
    }

    const handleAddEdgeButton = (id: string|number) => {
        if (activeTemplateId) addEdgeToPolygon(id, activeTemplateId, canvas)
    }

    const handleRemoveEdgeButton = (id: string|number) => {
        removeEdgeFromPolygon(id, canvas)
    }

    const handleRemoveSpotButton = (id: string|number) => {
        removeSpot(id, canvas)
    }

    useEffect(() => {
        canvas.renderAll()
    }, [spots, helpers])

    const toggleTemplatesSidebar = () => {
        setShowTemplatesSidebar(prev => !prev);
        if (!showTemplatesSidebar) setShowAddSectionSidebar(false);
    }

    const toggleAddSectionSidebar = () => {
        setShowAddSectionSidebar(prev => !prev);
        if (!showAddSectionSidebar) setShowTemplatesSidebar(false);
    }

    useEffect(() => {
        if (activeSpot) setShowTemplatesSidebar(false)
    }, [activeSpot]);

    return activeStep === 2 ? (
        <>
            <Heading level={3} classes={'mt-4'}>{t('progress.define_areas')}</Heading>
            <Text classes={'my-4'}>{t('define_areas.description')}</Text>
            <div className={cx(styles['page-wrapper'])}>
                <div ref={wrapper} className={cx(styles['wrapper-container'])}>
                    <Sidebar
                        templates={templates}
                        changeTemplate={changePage}
                        active={activeTemplateId}
                        show={showTemplatesSidebar}
                        toggleSidebar={toggleTemplatesSidebar}
                        colors={{background: '#FC0', text: '#000'}}
                    />
                    <div className={cx(styles['button-wrapper'])} style={{ top: showAddSectionSidebar ? '1rem' : '-15rem' }}>

                        <div onClick={() => addRectangle()} className={cx(styles['add-icon'])}>
                            <RectangleSVG />
                        </div>
                        <div onClick={() => addCircle()} className={cx(styles['add-icon'])}>
                            <CircleSVG />
                        </div>
                        <div onClick={() => addEllipse()} className={cx(styles['add-icon'])}>
                            <EllipseSVG />
                        </div>
                        <div onClick={() => addPolygon()} className={cx(styles['add-icon'])}>
                            <PolygonSVG />
                        </div>
                        <div>
                            <div
                                className={cx(styles['menu-icon'])}
                                onClick={toggleAddSectionSidebar}
                            >
                                <Icon
                                    icon={!showAddSectionSidebar ? faChevronDown : faChevronUp}
                                    size={'lg'}
                                    color={'#000'}
                                />
                            </div>
                            <div className={cx(styles['small-text'])}>
                                {t('define_areas.add_area')}
                            </div>
                        </div>
                    </div>
                </div>
                {activeTemplate &&
                    <div className={cx(styles['right-wrapper'])}>
                        <Heading level={3}>{activeTemplate.name}</Heading>
                        {activeSpot && <Heading level={4}>{t('define_areas.chosen')}</Heading>}
                        {activeSpot &&
                            <ActiveSpotCard
                                activeSpot={activeSpot}
                                removeSpot={handleRemoveSpotButton}
                                addEdge={handleAddEdgeButton}
                                removeEdge={handleRemoveEdgeButton}
                                changeExampleText={changeExampleText}
                                exampleTexts={examples}
                            />
                        }
                        {((spots.length === 1 && activeSpot === undefined) || spots.length >= 2) &&
                            <Heading level={4}>{t('define_areas.other')}</Heading>
                        }
                        {spots.map((spot) => {
                            if (spot.data.id !== activeSpotId && spot.data.template_id === activeTemplateId) return (
                                <Card title={spot.data.name} key={spot.data.id} classes={cx(styles['other-card'])}
                                      onClick={() => changeFabricActive(spot)}>
                                    <></>
                                </Card>
                            )
                            else return null
                        })}
                    </div>
                }
            </div>
        </>
    ) : null
}

export default DefineAreas;
